import { Heading2, Heading4, Caption, Heading3, Description } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import React from 'react'

import { Floor } from '@/components/Floor'
import Icon from '@/components/Icon'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { ButtonLink } from '@/design-lib/components/ButtonLink'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { affordabilityPath } from '@/i18n-routes'
import { ListingCate } from '@/page-commons/listingBySlug'
import { formatListingPricePerSquareMeter } from '@/utils/format-listing-pricePerSquareMeter'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import getTranslatedTitle from '@/utils/get-translated-title'
import { isLocaleEnabledFor } from '@/utils/locale-variables'
import { buildRelativeLocalizedLink } from '@/utils/navigation'
import { GTMEventCategory } from '@/utils/tracking/eventCategory'
import { handleClick } from '@/utils/tracking/eventsHandlers'

import { detailInfoData, pointShapeDivider } from './styles.css'

type Props = {
  cities: ContextValue
  listing: ListingCate
}

const DetailHeader: React.FC<Props> = ({ cities, listing }) => {
  const { lang, t } = useTranslation()
  const device = useDevice()

  const { buildingFloors, entireBuilding, entranceFloor, floorPosition } = listing.detail

  return (
    <>
      <div
        className={sprinkles({ alignItems: { desktop: 'center' }, display: { desktop: 'flex' }, marginBottom: 's-m' })}
      >
        <Description
          className={sprinkles({
            paddingBottom: { desktop: 0, mobile: 's' },
            textTransform: 'uppercase',
          })}
          color="greyscale600"
        >
          {cities.router.city?.translatedCityName}
        </Description>

        {listing.detail.zone && (
          <>
            {device === 'desktop' && <div className={pointShapeDivider} />}

            <Description color="greyscale500">{listing.detail.zone.label}</Description>
          </>
        )}
      </div>
      <Heading2
        as="h1"
        className={sprinkles({
          marginBottom: 'm',
        })}
      >
        {getTranslatedTitle(listing.detail?.titles, lang)}{' '}
      </Heading2>

      <div
        className={sprinkles({
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 'm',
        })}
      >
        <div
          className={sprinkles({
            columnGap: { tablet: '3xl' },
            display: { mobile: 'none', tablet: 'flex' },
            marginBottom: 'l',
          })}
        >
          <Heading4 as="p">{listing.detail.commercialArea} m&sup2;</Heading4>
          <Heading4 as="p">
            <Floor {...{ buildingFloors, entireBuilding, entranceFloor, floorPosition }} />
          </Heading4>
        </div>
        <div
          className={sprinkles({
            alignItems: { desktop: 'center' },
            display: { desktop: 'flex' },
            gap: { desktop: 'xl' },
            marginBottom: { desktop: 'l' },
          })}
        >
          <div>
            <Heading3 as="p" className={sprinkles({ paddingBottom: { mobile: 'xs' } })}>
              {formatListingSellingPrice(listing.detail.privateNegotiation, listing.detail.sellingPrices, t, lang)}
            </Heading3>
            {!listing.detail.privateNegotiation && (
              <div className={sprinkles({ display: { tablet: 'none' } })}>
                <Caption>
                  {formatListingPricePerSquareMeter(listing.detail.sellingPrices, listing.detail.commercialArea, lang)}
                </Caption>
              </div>
            )}

            {!listing.detail.privateNegotiation && (
              <div
                className={sprinkles({
                  display: {
                    mobile: 'none',
                    tablet: 'flex',
                  },
                })}
              >
                <Caption color="greyscale500">
                  {formatListingPricePerSquareMeter(listing.detail.sellingPrices, listing.detail.commercialArea, lang)}
                </Caption>
              </div>
            )}
          </div>

          {isLocaleEnabledFor('affordability', lang) && !listing.detail.privateNegotiation && (
            <ButtonLink
              iconRight
              className={sprinkles({ marginY: { desktop: 0, mobile: 'm' } })}
              href={`${buildRelativeLocalizedLink(affordabilityPath, lang)}?listing=${listing.id}`}
              Icon={() => <Icon.Chevron />}
              label={t('affordability:Affordability_ListingDetailCTA')}
              sizeVariant="small"
              style={{ backgroundColor: '#D6F5FF', color: theme.colors.greyscale600 }}
              onClick={() => handleClick(GTMEventCategory.Affordability, 'StartFlow', 'FromListingDetail')}
            />
          )}
        </div>

        <div className={detailInfoData}>
          <Heading4 as="p">{listing.detail.commercialArea} m&sup2;</Heading4>
          <Floor {...{ buildingFloors, entireBuilding, entranceFloor, floorPosition }} />
        </div>
      </div>
    </>
  )
}
export default DetailHeader
